import classNames from 'classnames';
import React, { HTMLAttributes, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateValue, useFormatDate } from '../../../hooks/useFormatDate';
import { Tabs } from '../../../modules/renderer/RendererRightPanel';
import { T, useT } from '../../../translation/src';
import {
  GeneralStatus,
  NotificationType,
  ProjectInviteNotification as ProjectInviteNotificationType,
  PointCloudCalculationDoneNotification as PointCloudCalculationDoneNotificationType,
  PointCloudConvertDoneNotification as PointCloudConvertDoneNotificationType,
  PointCloudExportDoneNotification,
  ProjectAwaitingCreditsNotification as ProjectAwaitingCreditsNotificationType,
  ProjectCustomDealNotification as ProjectCustomDealNotificationType,
  SalesRequestNotification as SalesRequestNotificationType,
  UploadStatus,
  UserAcceptedInvitationNotification as UserAcceptedInvitationNotificationType,
  UserGotMentionedNotification as UserGotMentionedNotificationType,
  useNotificationsDeleteMutation,
  useNotificationsMarkAsReadMutation,
  useNotificationsQuery,
} from '../../../types/graphqlTypes';
import { createURLParams } from '../../../utils/URLSearchParams';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useDownloadExport } from '../../../hooks/useDownloadExport';
import { useSubscriptionTypeTranslations } from '../../../hooks/translations/useSubscriptionTypeTranslations';

const useReadNotification = () => {
  const [readNotification] = useNotificationsMarkAsReadMutation();
  const onReadNotification = useCallback(
    ({ notification }: { notification: { id: string; __typename?: string } }) => {
      readNotification({
        variables: { ids: [notification.id] },
        update: (cache) => {
          cache.modify({
            id: cache.identify(notification),
            fields: {
              isRead() {
                return true;
              },
            },
          });
        },
      });
    },
    [readNotification],
  );
  return onReadNotification;
};

const NotificationDate: React.FC<{ date: DateValue }> = ({ date }) => {
  const { formatDateAndTime } = useFormatDate();
  return <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateAndTime(date)}</span>;
};

const useDeleteNotification = () => {
  const [deleteNotification] = useNotificationsDeleteMutation();
  return useCallback(
    (notification: { id: string; __typename?: string }) => {
      deleteNotification({
        variables: { ids: [notification.id] },
        update: (cache) => {
          cache.evict({ id: cache.identify(notification) });
          cache.gc();
        },
      });
    },
    [deleteNotification],
  );
};

interface NotificationContainerProps extends React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  children: React.ReactNode;
  notification: { id: string; __typename?: string; isRead?: boolean | null };
  /** Buttons shown before the delete button, e.g. a download */
  actions?: React.ReactNode;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({ children, notification, actions, ...props }) => {
  const deleteNotification = useDeleteNotification();
  const deleteTitle = useT('delete', { swc: true });

  const onDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      deleteNotification(notification);
    },
    [deleteNotification, notification],
  );

  return (
    <div
      className={classNames(
        'w-full cursor-pointer flex flex-row py-3 px-3 wrap-break-word gap-2.5 items-center relative border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-neon-green-300 dark:hover:bg-black',
        // Unread: a green line on the left edge; an inset shadow, so the text does not shift.
        !notification.isRead && 'shadow-[inset_4px_0_0_var(--color-green-500)] hover:shadow-[inset_5px_0_0_var(--color-green-500)] transition-shadow',
        // Read: muted text, back to full colour on hover.
        notification.isRead && 'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white',
      )}
      {...props}
    >
      <div className={classNames('flex flex-col flex-1 min-w-0', !notification.isRead && 'font-bold')}>{children}</div>
      <div className="shrink-0 flex items-center gap-2">
        {actions}
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center justify-center p-1 rounded text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 cursor-pointer transition-colors"
          title={deleteTitle}
          aria-label={deleteTitle}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const PointCloudCalculationDoneNotification_: React.FC<{
  notification: PointCloudCalculationDoneNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const link = `/organisations/${notification.organisationId}/projects/${notification.meta.projectId}${
    !notification.meta.annotationIdentifier
      ? ''
      : '?' +
        createURLParams({
          params: {
            rightPanelTab: Tabs.CALCULATIONS,
            annotationIdentifier: notification.meta.annotationIdentifier,
          },
        })
  }`;

  const onClick = useCallback(() => {
    readNotification({ notification });
    if (link) {
      window.location.replace(link);
    }
  }, [readNotification, notification, link]);

  const status = notification.meta.status;

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        <T _str="pointcloud calculation on" /> {notification.meta.projectName} {status && status === GeneralStatus.Success ? <T _str="succeeded" /> : <T _str="failed" />}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const PointCloudCalculationDoneNotification = memo(PointCloudCalculationDoneNotification_);

const PointCloudConvertDoneNotification_: React.FC<{
  notification: PointCloudConvertDoneNotificationType & {
    meta: PointCloudConvertDoneNotificationType['meta'] & { uploadStatus?: UploadStatus | null };
  };
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const succeeded = notification.meta.uploadStatus === UploadStatus.Success;
  const onClick = useCallback(() => {
    readNotification({ notification });
    // A failed project has nothing to open: its organisation's projects, where it is listed with its error.
    window.location.replace(succeeded ? `/organisations/${notification.organisationId}/projects/${notification.meta.projectId}` : `/organisations/${notification.organisationId}/projects`);
  }, [readNotification, notification, succeeded]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span className={classNames(!succeeded && 'text-error-red-500')}>
        <T _str="pointcloud convert on" /> {notification.meta.projectName} {succeeded ? <T _str="succeeded" /> : <T _str="failed" />}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const PointCloudConvertDoneNotification = memo(PointCloudConvertDoneNotification_);

const UserAcceptedInvitationNotification_: React.FC<{
  notification: UserAcceptedInvitationNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    readNotification({ notification });
    navigate(`/organisations/${notification.organisationId}/users`);
  }, [readNotification, notification, navigate]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {notification.meta.userName} <T _str="joined organisation" /> {notification.meta.organisationName}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const UserAcceptedInvitationNotification = memo(UserAcceptedInvitationNotification_);

const PointcloudExportDoneNotification_: React.FC<{
  notification: PointCloudExportDoneNotification;
  onClose: () => void;
}> = ({ notification, onClose }) => {
  const readNotification = useReadNotification();
  const navigate = useNavigate();
  const { downloadExport } = useDownloadExport();

  const onClick = useCallback(() => {
    readNotification({ notification });
    navigate(`/organisations/${notification.organisationId}/projects/${notification.meta.projectId}`);
    onClose();
  }, [readNotification, notification, navigate, onClose]);

  const onDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const { thisExport } = notification.meta;
      if (!thisExport?.exportId) return;

      readNotification({ notification });

      await downloadExport({
        projectId: notification.meta.projectId,
        exportId: thisExport.exportId,
      });
    },
    [notification, downloadExport, readNotification],
  );

  const downloadTitle = useT('download', { swc: true });
  const exportTypeLabel = 'Point Cloud';
  const status = notification.meta.status;
  const hasDownload = status === GeneralStatus.Success && notification.meta.thisExport?.exportId;

  return (
    <NotificationContainer
      onClick={onClick}
      notification={notification}
      actions={
        hasDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="flex items-center justify-center p-1.5 rounded bg-french-violet-600 text-white cursor-pointer transition-all hover:brightness-110 active:brightness-95"
            title={downloadTitle}
            aria-label={downloadTitle}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
        )
      }
    >
      <span>
        {exportTypeLabel} <T _str="export done on " /> {notification.meta.projectName}
        {status && status === GeneralStatus.Success ? '' : ` - ${status}`}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const PointcloudExportDoneNotification = memo(PointcloudExportDoneNotification_);

const UserGotMentionedNotification_: React.FC<{
  notification: UserGotMentionedNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const link = `/organisations/${notification.organisationId}/projects/${notification.meta.projectId}${
    !notification.meta.referenceObjectIdentifier
      ? ''
      : '?' +
        createURLParams({
          params: {
            rightPanelTab: Tabs.LAYERS,
            annotationIdentifier: notification.meta.referenceObjectIdentifier,
          },
        })
  }`;

  const onClick = useCallback(() => {
    readNotification({ notification });
    if (link) {
      window.location.replace(link);
    }
  }, [readNotification, notification, link]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {notification.meta.mentioningUserName} <T _str="has mentioned you in a comment" />
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const UserGotMentionedNotification = memo(UserGotMentionedNotification_);

const CreditsPurchasedNotification_: React.FC<{
  notification: NotificationOf<'CreditsPurchasedNotification'>;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const invoice = notification.ledgerEntry;
  const onClick = useCallback(() => {
    readNotification({ notification });
    if (invoice?.odooInvoiceUrl) window.open(invoice.odooInvoiceUrl, '_blank', 'noopener,noreferrer');
  }, [readNotification, notification, invoice]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        <T _str="{userName} purchased {amount} projects for {organisationName}. Invoice needed" args={{ userName: notification.meta.userName, amount: notification.meta.amount, organisationName: notification.meta.organisationName }} />
      </span>
      {/* The invoice it is about: its number once posted, the draft in Odoo until then. */}
      {invoice?.odooInvoiceId && <span className="text-xs underline text-gray-500 dark:text-gray-400">{invoice.odooInvoiceNumber ?? <T _str="draft invoice #{id}" args={{ id: invoice.odooInvoiceId }} />}</span>}
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const CreditsPurchasedNotification = memo(CreditsPurchasedNotification_);

const SubscriptionPurchasedNotification_: React.FC<{
  notification: NotificationOf<'SubscriptionPurchasedNotification'>;
}> = ({ notification }) => {
  const readNotification = useReadNotification();
  const plans = useSubscriptionTypeTranslations();
  const invoice = notification.ledgerEntry;
  const { userName, organisationName, subscriptionType, fromSubscriptionType } = notification.meta;
  const onClick = useCallback(() => {
    readNotification({ notification });
    if (invoice?.odooInvoiceUrl) window.open(invoice.odooInvoiceUrl, '_blank', 'noopener,noreferrer');
  }, [readNotification, notification, invoice]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {fromSubscriptionType ? (
          <T _str="{userName} upgraded {organisationName} from {fromPlan} to {plan}. Invoice needed." args={{ userName, organisationName, fromPlan: plans[fromSubscriptionType], plan: plans[subscriptionType] }} />
        ) : (
          <T _str="{userName} bought a {plan} subscription for {organisationName}. Invoice needed." args={{ userName, organisationName, plan: plans[subscriptionType] }} />
        )}
      </span>
      {/* The invoice it is about: its number once posted, the draft in Odoo until then. */}
      {invoice?.odooInvoiceId && <span className="text-xs underline text-gray-500 dark:text-gray-400">{invoice.odooInvoiceNumber ?? <T _str="draft invoice #{id}" args={{ id: invoice.odooInvoiceId }} />}</span>}
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const SubscriptionPurchasedNotification = memo(SubscriptionPurchasedNotification_);

const ProjectAwaitingCreditsNotification_: React.FC<{
  notification: ProjectAwaitingCreditsNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const onClick = useCallback(() => {
    readNotification({ notification });
    // The project card on the projects page carries the charge & activate flow.
    window.location.replace(`/organisations/${notification.organisationId}/projects`);
  }, [readNotification, notification]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        <T _str="{projectName} is processed and requires {requiredCredits} projects to activate" args={{ projectName: notification.meta.projectName, requiredCredits: notification.meta.requiredCredits }} />
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const ProjectAwaitingCreditsNotification = memo(ProjectAwaitingCreditsNotification_);

const ProjectCustomDealNotification_: React.FC<{
  notification: ProjectCustomDealNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const onClick = useCallback(() => {
    readNotification({ notification });
  }, [readNotification, notification]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {notification.meta.projectName} <T _str="exceeds the custom-deal point threshold" /> ({notification.meta.pointCountTotal.toLocaleString()} <T _str="points" />
        ). <T _str="Contact the organisation" />
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const ProjectCustomDealNotification = memo(ProjectCustomDealNotification_);

// A customer's message to sales: who asked, for which organisation, and the message itself.
const SalesRequestNotification_: React.FC<{
  notification: SalesRequestNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const onClick = useCallback(() => {
    readNotification({ notification });
  }, [readNotification, notification]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span className="flex flex-col gap-1">
        <span>
          {notification.meta.userName} ({notification.meta.userEmail}) <T _str="contacted sales for" /> {notification.meta.organisationName}
        </span>
        <span className="text-sm italic text-gray-500 dark:text-gray-400 whitespace-pre-line line-clamp-3">{notification.meta.message}</span>
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const SalesRequestNotification = memo(SalesRequestNotification_);

// To a partner: a customer billed to it renews soon (it may still cancel), or just renewed. Opens its customers.
const CustomerRenewalNotification_: React.FC<{
  notification: NotificationOf<'CustomerRenewalNotification'>;
}> = ({ notification }) => {
  const readNotification = useReadNotification();
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const navigate = useNavigate();
  const { customerName, subscriptionType, renewsAt } = notification.meta;
  const onClick = useCallback(() => {
    readNotification({ notification });
    navigate(`/organisations/${notification.organisationId}/referrals/customers`);
  }, [navigate, notification, readNotification]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        <span className="font-bold">{customerName}</span> – {plans[subscriptionType]} –{' '}
        {notification.type === NotificationType.CustomerRenewed ? <T _str="renewed on {date}" args={{ date: formatDay(renewsAt) }} /> : <T _str="renews on {date}" args={{ date: formatDay(renewsAt) }} />}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const CustomerRenewalNotification = memo(CustomerRenewalNotification_);

/** Typed from the query result rather than the schema type: the query aliases two meta fields. */
type OrganisationInviteNotificationItem = Extract<AnyNotification, { __typename?: 'OrganisationInviteNotification' }>;

// The invitation mail's link, in-app: the invitation page (outside the logged-in shell) accepts it.
const OrganisationInviteNotification_: React.FC<{
  notification: OrganisationInviteNotificationItem;
}> = ({ notification }) => {
  const readNotification = useReadNotification();
  const navigate = useNavigate();
  const { inviteToken, inviteProjectId: projectId, inviteProjectName: projectName, invitedByName, organisationName } = notification.meta;

  const onClick = useCallback(() => {
    readNotification({ notification });
    const params = createURLParams({ params: { token: inviteToken, organisationId: notification.organisationId, ...(projectId ? { projectId } : {}) } });
    navigate(`/register-invite?${params}`);
  }, [readNotification, notification, navigate, inviteToken, projectId]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {invitedByName} <T _str="invited you to" /> {organisationName}
        {projectName && (
          <>
            {' '}
            <T _str="for the project" /> {projectName}
          </>
        )}
      </span>
      <span className="text-xs text-french-violet-500 dark:text-neon-green-400">
        <T _str="click to accept the invitation" swc />
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const OrganisationInviteNotification = memo(OrganisationInviteNotification_);

const ProjectInviteNotification_: React.FC<{
  notification: ProjectInviteNotificationType;
}> = ({ notification }) => {
  const readNotification = useReadNotification();

  const onClick = useCallback(() => {
    readNotification({ notification });
    // Full navigation, like the other project links: leaving a running viewer through the router is unsafe.
    window.location.replace(`/organisations/${notification.organisationId}/projects/${notification.meta.projectId}`);
  }, [readNotification, notification]);

  return (
    <NotificationContainer onClick={onClick} notification={notification}>
      <span>
        {notification.meta.invitedByName} <T _str="gave you access to the project" /> {notification.meta.projectName}
      </span>
      <NotificationDate date={notification.createdAt} />
    </NotificationContainer>
  );
};
const ProjectInviteNotification = memo(ProjectInviteNotification_);

type AnyNotification = NonNullable<ReturnType<typeof useNotificationsQuery>['data']>['notifications'][number];
// One kind of notification as the query selects it (its ledger row, for one, only carries the invoice fields).
type NotificationOf<Typename extends NonNullable<AnyNotification['__typename']>> = Extract<AnyNotification, { __typename?: Typename }>;

interface NotificationListItemProps {
  notification: AnyNotification;
  onClose?: () => void;
}

/**
 * Renders a single notification row, dispatching on the notification __typename.
 * Shared between the sidebar and viewer header notification dropdowns.
 */
export const NotificationListItem: React.FC<NotificationListItemProps> = ({ notification, onClose }) => {
  if (notification.__typename === 'PointCloudConvertDoneNotification') return <PointCloudConvertDoneNotification notification={notification} />;
  if (notification.__typename === 'PointCloudCalculationDoneNotification') return <PointCloudCalculationDoneNotification notification={notification} />;
  if (notification.__typename === 'UserAcceptedInvitationNotification') return <UserAcceptedInvitationNotification notification={notification} />;
  if (notification.__typename === 'PointCloudExportDoneNotification') return <PointcloudExportDoneNotification notification={notification} onClose={onClose ?? (() => undefined)} />;
  if (notification.__typename === 'UserGotMentionedNotification') return <UserGotMentionedNotification notification={notification} />;
  if (notification.__typename === 'CreditsPurchasedNotification') return <CreditsPurchasedNotification notification={notification} />;
  if (notification.__typename === 'SubscriptionPurchasedNotification') return <SubscriptionPurchasedNotification notification={notification} />;
  if (notification.__typename === 'ProjectAwaitingCreditsNotification') return <ProjectAwaitingCreditsNotification notification={notification} />;
  if (notification.__typename === 'ProjectCustomDealNotification') return <ProjectCustomDealNotification notification={notification} />;
  if (notification.__typename === 'SalesRequestNotification') return <SalesRequestNotification notification={notification} />;
  if (notification.__typename === 'OrganisationInviteNotification') return <OrganisationInviteNotification notification={notification} />;
  if (notification.__typename === 'ProjectInviteNotification') return <ProjectInviteNotification notification={notification} />;
  if (notification.__typename === 'CustomerRenewalNotification') return <CustomerRenewalNotification notification={notification} />;
  return null;
};
